import React, { useEffect, useState, useCallback } from 'react';
import { api } from '../../lib/api';
import { Loader2, TrendingUp, Users, ShieldCheck, MapPin, Radio } from 'lucide-react';

const FUEL_LABELS = { vpower: 'V-Power', super: 'Súper', regular: 'Regular', diesel: 'Diesel', ulsd: 'ULSD' };
const FUEL_COLORS = { vpower: '#3b6cff', super: '#0f1d6f', regular: '#15803d', diesel: '#92400e', ulsd: '#991b1b' };
const FUELS = ['vpower', 'super', 'regular', 'diesel', 'ulsd'];
const HEATMAP_FUELS = ['super', 'regular', 'diesel', 'ulsd', 'vpower'];
const HEATMAP_DOT = { super: '#3b6cff', regular: '#15803d', diesel: '#d97706', ulsd: '#ea580c', vpower: '#7c3aed' };

// Diff color: negative (cheaper) → green; positive (expensive) → red.
// Magnitude maps to opacity. >= 0.30 Q → full color; |diff| < 0.005 → neutral.
const diffCellStyle = (diff) => {
  if (diff == null) return { background: '#fafaf6', color: '#a8a397' };
  const absD = Math.abs(diff);
  if (absD < 0.005) return { background: '#f8f6f1', color: '#3a3a3a' };
  const t = Math.min(1, absD / 0.30);
  // Soft red/green gradient
  if (diff > 0) {
    // red — interpolate from #fee2e2 (t=0) to #dc2626 (t=1)
    const r = Math.round(254 - (254 - 220) * t);
    const g = Math.round(226 - (226 - 38) * t);
    const b = Math.round(226 - (226 - 38) * t);
    return { background: `rgb(${r},${g},${b})`, color: t > 0.5 ? '#fff' : '#7f1d1d' };
  }
  // green — interpolate from #dcfce7 (t=0) to #16a34a (t=1)
  const r = Math.round(220 - (220 - 22) * t);
  const g = Math.round(252 - (252 - 163) * t);
  const b = Math.round(231 - (231 - 74) * t);
  return { background: `rgb(${r},${g},${b})`, color: t > 0.5 ? '#fff' : '#14532d' };
};
const fmtDiff = (d) => d == null ? '—' : (d > 0 ? `+${d.toFixed(2)}` : d.toFixed(2));

const Kpi = ({ label, value, sub, tone = 'default', testid }) => {
  const cls = tone === 'green' ? 'text-[#15803d]' : tone === 'red' ? 'text-[#991b1b]' : tone === 'blue' ? 'text-[#3b6cff]' : 'text-[#1c1c1c]';
  return (
    <div className="bg-white border border-[#e8e5de] rounded-xl p-4" data-testid={testid}>
      <p className="text-[10.5px] uppercase tracking-wider text-[#8a8579]">{label}</p>
      <p className={`font-serif text-[32px] tabular-nums leading-none mt-1 ${cls}`}>{value}</p>
      {sub && <p className="text-[11px] text-[#5a5a5a] mt-1">{sub}</p>}
    </div>
  );
};

const FunnelBar = ({ label, value, max, color, testid }) => {
  const pct = max > 0 ? Math.min(100, Math.round((value / max) * 100)) : 0;
  return (
    <div data-testid={testid}>
      <div className="flex items-baseline justify-between text-[11.5px] mb-1">
        <span className="text-[#3a3a3a]">{label}</span>
        <span className="font-mono tabular-nums text-[#1c1c1c] font-medium">{value}</span>
      </div>
      <div className="h-2 bg-[#f0ede5] rounded-full overflow-hidden">
        <div className="h-full transition-all" style={{ width: `${pct}%`, background: color }}/>
      </div>
    </div>
  );
};

// Daily SLA compliance heatmap (4 rows × N days)
const SLA_BUCKET_CLS = {
  green: 'bg-[#10b981]',
  orange: 'bg-[#f59e0b]',
  red: 'bg-[#dc2626]',
  muted: 'bg-[#e8e5de]',
};
const SLA_LABEL = { green: '≥ 80% cumplimiento', orange: '50–80% cumplimiento', red: '< 50% cumplimiento', muted: 'Sin datos' };

const SLAHeatmap = ({ data, days }) => {
  if (!data || !data.dates || data.dates.length === 0) {
    return (
      <div className="bg-white border border-[#e8e5de] rounded-xl p-8 text-center text-[12.5px] text-[#8a8579]" data-testid="sla-heatmap-empty">
        Sin datos de SLA todavía.
      </div>
    );
  }
  const rows = [
    { id: 'cob',    label: 'Cob.',    full: 'Cobertura de captura' },
    { id: 'tiempo', label: 'Tiempo',  full: 'Captura a tiempo' },
    { id: 'cumpl',  label: 'Compl.',  full: 'Cumplimiento (evidencia)' },
    { id: 'ocr',    label: 'OCR',     full: 'OCR sin discrepancias' },
  ];
  const fmtDay = (iso) => {
    if (!iso) return '';
    const d = new Date(iso + 'T12:00:00');
    return d.getDate().toString();
  };
  return (
    <div className="bg-white border border-[#e8e5de] rounded-2xl p-5" data-testid="sla-heatmap">
      <div className="flex items-baseline justify-between mb-4 gap-2 flex-wrap">
        <div>
          <h4 className="font-serif text-[20px] tracking-tight leading-tight">Cumplimiento diario</h4>
          <p className="text-[11.5px] text-[#5a5a5a] mt-0.5">SLA por día · últimos {days} días</p>
        </div>
        <div className="flex items-center gap-3 text-[10.5px] text-[#5a5a5a]">
          {['green', 'orange', 'red'].map(k => (
            <span key={k} className="inline-flex items-center gap-1.5">
              <span className={`inline-block w-3 h-3 rounded-sm ${SLA_BUCKET_CLS[k]}`}/>
              {SLA_LABEL[k]}
            </span>
          ))}
        </div>
      </div>
      <div className="overflow-x-auto" data-testid="sla-grid-scroll">
        <div className="min-w-[640px] space-y-1.5">
          {rows.map(r => (
            <div key={r.id} className="grid grid-cols-[64px_1fr] items-center gap-2.5">
              <span className="text-[11.5px] text-[#5a5a5a] text-right" title={r.full}>{r.label}</span>
              <div className="grid gap-[3px]" style={{ gridTemplateColumns: `repeat(${data.dates.length}, 1fr)` }} data-testid={`sla-row-${r.id}`}>
                {(data[r.id] || []).map((bucket, i) => (
                  <div
                    key={`${r.id}-${data.dates[i] || i}`}
                    className={`h-7 rounded-sm ${SLA_BUCKET_CLS[bucket] || SLA_BUCKET_CLS.muted}`}
                    data-testid={`sla-cell-${r.id}-${i}`}
                    title={`${data.dates[i]} · ${r.full}: ${SLA_LABEL[bucket] || bucket}`}
                  />
                ))}
              </div>
            </div>
          ))}
          {/* Day-of-month labels */}
          <div className="grid grid-cols-[64px_1fr] gap-2.5 pt-1">
            <div/>
            <div className="grid gap-[3px]" style={{ gridTemplateColumns: `repeat(${data.dates.length}, 1fr)` }} data-testid="sla-date-axis">
              {data.dates.map((d, i) => {
                // Show every nth label to avoid clutter when range is long
                const step = data.dates.length > 31 ? 5 : data.dates.length > 14 ? 2 : 1;
                const show = i % step === 0;
                return (
                  <span key={d} className="text-[9px] text-[#8a8579] tabular-nums text-center">
                    {show ? fmtDay(d) : ''}
                  </span>
                );
              })}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

const formatMin = (m) => {
  if (m == null) return '—';
  if (m < 60) return `${m}m`;
  const h = Math.floor(m / 60);
  return `${h}h ${m % 60}m`;
};

const AdminMetrics = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [days, setDays] = useState(30);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get('/admin/metrics', { params: { days } });
      setData(r.data);
    } finally { setLoading(false); }
  }, [days]);
  useEffect(() => { load(); }, [load]);

  if (loading && !data) return <div className="flex justify-center py-20"><Loader2 className="animate-spin text-[#8a8579]"/></div>;
  if (!data) return null;

  const { by_department, competitive_diff_by_zone, daily_sla, tracker_performance, agent_performance, tm_performance, price_change_funnel: fn } = data;
  const maxDeptTotal = Math.max(1, ...by_department.map(d => d.total));

  return (
    <div className="space-y-7" data-testid="admin-metrics-page">
      {/* Header + range */}
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <h2 className="font-serif text-[40px] leading-tight tracking-tight">Métricas de operación</h2>
          <p className="text-[13px] text-[#5a5a5a] mt-1">Performance de equipos y cambios de precio para aprobadores.</p>
        </div>
        <select
          value={days}
          onChange={e => setDays(Number(e.target.value))}
          className="h-9 text-[12.5px] border border-[#e8e5de] rounded-md px-3 bg-white"
          data-testid="metrics-range-select"
        >
          <option value={1}>Hoy</option>
          <option value={7}>Últimos 7 días</option>
          <option value={30}>Últimos 30 días</option>
          <option value={90}>Últimos 90 días</option>
        </select>
      </div>

      {/* ===== Funnel cambios de precio ===== */}
      <section className="space-y-3" data-testid="section-funnel">
        <h3 className="font-serif text-[22px] tracking-tight inline-flex items-center gap-2"><Radio size={16} strokeWidth={1.6}/> Cambios de precio</h3>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          <Kpi testid="kpi-requested" label="Solicitados" value={fn.requested}/>
          <Kpi testid="kpi-approved" label="Aprobados" value={fn.approved} sub={`${fn.approval_rate_pct}% del total`} tone="green"/>
          <Kpi testid="kpi-rejected" label="Rechazados" value={fn.rejected} tone="red"/>
          <Kpi testid="kpi-pending" label="Pendientes" value={fn.pending} tone="blue"/>
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          <Kpi testid="kpi-autopush" label="Auto-push generados" value={fn.auto_pushed}/>
          <Kpi testid="kpi-evidence" label="Evidencias enviadas" value={fn.confirmed_evidence} sub={`${fn.evidence_rate_pct}% confirmado`} tone="green"/>
          <Kpi testid="kpi-approver-median" label="Tiempo aprobador (mediana)" value={formatMin(fn.approver_median_min)} sub={`p90: ${formatMin(fn.approver_p90_min)}`}/>
          <Kpi testid="kpi-evidence-pending" label="Evidencias pendientes" value={fn.pending_evidence} tone={fn.pending_evidence > 0 ? 'red' : 'default'}/>
        </div>
        {/* SLA daily compliance heatmap (replaces funnel bars) */}
        <SLAHeatmap data={daily_sla} days={days}/>
      </section>

      {/* ===== Competitive diff heatmap by zone ===== */}
      <section className="space-y-3" data-testid="section-competitive-diff">
        <div>
          <h3 className="font-serif text-[22px] tracking-tight">Diferencial competitivo por zona</h3>
          <p className="text-[12px] text-[#5a5a5a] mt-0.5">Tu precio vigente vs. el precio que deberías tener por reglas (mín. de markers + diferenciales de Parámetros) — verde = por debajo del objetivo, rojo = por arriba</p>
        </div>
        {(competitive_diff_by_zone || []).length === 0 ? (
          <div className="bg-white border border-[#e8e5de] rounded-xl p-8 text-center text-[12.5px] text-[#8a8579]">
            Sin datos de competencia disponibles. Configura markers en tus estaciones.
          </div>
        ) : (
          <div className="bg-white border border-[#e8e5de] rounded-xl p-4 overflow-x-auto" data-testid="diff-heatmap">
            <div className="min-w-[640px]">
              {/* Header row */}
              <div className="grid grid-cols-[140px_repeat(5,1fr)_90px_110px] gap-2 mb-2 text-[11px] uppercase tracking-wider text-[#8a8579]">
                <div>Zona</div>
                {HEATMAP_FUELS.map(f => (
                  <div key={f} className="inline-flex items-center gap-1.5 justify-center">
                    <span className="inline-block w-1.5 h-1.5 rounded-full" style={{ background: HEATMAP_DOT[f] }}/>
                    {FUEL_LABELS[f]}
                  </div>
                ))}
                <div className="text-right">Promedio</div>
                <div className="text-right">Cambios %</div>
              </div>
              {/* Rows */}
              {competitive_diff_by_zone.map(row => (
                <div key={row.zone} className="grid grid-cols-[140px_repeat(5,1fr)_90px_110px] gap-2 mb-2" data-testid={`diff-row-${row.zone.replace(/\s+/g, '-')}`}>
                  <div className="flex items-center text-[12.5px] text-[#3a3a3a] min-w-0">
                    <p className="truncate">{row.zone}</p>
                    <span className="ml-2 text-[10px] text-[#a8a397]">·{row.station_count}</span>
                  </div>
                  {HEATMAP_FUELS.map(f => {
                    const v = row[f];
                    const style = diffCellStyle(v);
                    return (
                      <div
                        key={f}
                        className="h-10 rounded-md flex items-center justify-center text-[12.5px] font-mono tabular-nums font-medium transition"
                        style={style}
                        data-testid={`diff-cell-${row.zone.replace(/\s+/g, '-')}-${f}`}
                        title={v != null ? `${FUEL_LABELS[f]}: ${fmtDiff(v)} Q` : 'Sin datos'}
                      >
                        {fmtDiff(v)}
                      </div>
                    );
                  })}
                  {/* Avg cell */}
                  <div
                    className="h-10 rounded-md flex items-center justify-center text-[12.5px] font-mono tabular-nums font-medium"
                    style={diffCellStyle(row.avg)}
                    data-testid={`diff-cell-${row.zone.replace(/\s+/g, '-')}-avg`}
                  >
                    {fmtDiff(row.avg)}
                  </div>
                  {/* Changes count + share */}
                  <div
                    className="h-10 rounded-md flex flex-col items-end justify-center text-[#3a3a3a] px-2.5 bg-[#f8f6f1]"
                    data-testid={`diff-cell-${row.zone.replace(/\s+/g, '-')}-changes`}
                    title={`${row.changes_count} cambios aprobados · ${row.changes_pct}% del total`}
                  >
                    <span className="text-[13px] font-mono tabular-nums font-medium leading-tight">{row.changes_count}</span>
                    <span className="text-[10.5px] text-[#5a5a5a] tabular-nums">{row.changes_pct}%</span>
                  </div>
                </div>
              ))}
              {/* Legend */}
              <div className="mt-4 flex items-center justify-end gap-3 text-[10.5px] text-[#8a8579]">
                <span>Por debajo del objetivo</span>
                <span className="h-2 w-32 rounded-full" style={{ background: 'linear-gradient(90deg, #16a34a 0%, #f8f6f1 50%, #dc2626 100%)' }}/>
                <span>Por arriba del objetivo</span>
              </div>
            </div>
          </div>
        )}
      </section>

      {/* ===== Por departamento (con breakdown por producto) ===== */}
      <section className="space-y-3" data-testid="section-departments">
        <h3 className="font-serif text-[22px] tracking-tight inline-flex items-center gap-2"><MapPin size={16} strokeWidth={1.6}/> Cambios por departamento</h3>
        {by_department.length === 0 ? (
          <div className="bg-white border border-[#e8e5de] rounded-xl p-8 text-center text-[12.5px] text-[#8a8579]">Sin cambios aprobados en este rango.</div>
        ) : (
          <div className="bg-white border border-[#e8e5de] rounded-xl overflow-hidden">
            <div className="grid grid-cols-[1fr_60px_repeat(5,60px)] gap-2 px-4 py-2.5 bg-[#f8f6f1] border-b border-[#e8e5de] text-[10.5px] uppercase tracking-wider text-[#8a8579]">
              <div>Departamento</div>
              <div className="text-right">Total</div>
              {FUELS.map(f => <div key={f} className="text-right">{FUEL_LABELS[f]}</div>)}
            </div>
            <div className="divide-y divide-[#f0ede5]">
              {by_department.map(d => (
                <div key={d.department} className="grid grid-cols-[1fr_60px_repeat(5,60px)] gap-2 px-4 py-2.5 items-center text-[12.5px]" data-testid={`dept-row-${d.department.replace(/\s+/g, '-')}`}>
                  <div className="min-w-0">
                    <p className="truncate font-medium">{d.department}</p>
                    <div className="h-1 mt-1.5 bg-[#f0ede5] rounded-full overflow-hidden">
                      <div className="h-full bg-[#0f1d6f]" style={{ width: `${(d.total / maxDeptTotal) * 100}%` }}/>
                    </div>
                  </div>
                  <div className="text-right tabular-nums font-medium">{d.total}</div>
                  {FUELS.map(f => (
                    <div key={f} className="text-right tabular-nums" style={{ color: d[f] > 0 ? FUEL_COLORS[f] : '#cfcabe' }}>{d[f] || '—'}</div>
                  ))}
                </div>
              ))}
            </div>
          </div>
        )}
      </section>

      {/* ===== Trackers ===== */}
      <section className="space-y-3" data-testid="section-trackers">
        <h3 className="font-serif text-[22px] tracking-tight inline-flex items-center gap-2"><Users size={16} strokeWidth={1.6}/> Performance · Trackers</h3>
        {tracker_performance.length === 0 ? (
          <div className="bg-white border border-[#e8e5de] rounded-xl p-8 text-center text-[12.5px] text-[#8a8579]">Sin actividad de trackers.</div>
        ) : (
          <div className="bg-white border border-[#e8e5de] rounded-xl overflow-hidden">
            <div className="grid grid-cols-[1.6fr_80px_80px_80px_90px_90px_90px] gap-2 px-4 py-2.5 bg-[#f8f6f1] border-b border-[#e8e5de] text-[10.5px] uppercase tracking-wider text-[#8a8579]">
              <div>Tracker</div>
              <div className="text-right">Capturas</div>
              <div className="text-right">A tiempo</div>
              <div className="text-right">No realiz.</div>
              <div className="text-right">% Puntual</div>
              <div className="text-right">Track. solic.</div>
              <div className="text-right">% Aprob.</div>
            </div>
            <div className="divide-y divide-[#f0ede5]">
              {tracker_performance.map(t => {
                const punctualCls = t.on_time_pct >= 80 ? 'text-[#15803d]' : t.on_time_pct >= 50 ? 'text-[#92400e]' : 'text-[#991b1b]';
                return (
                  <div key={t.user_id} className="grid grid-cols-[1.6fr_80px_80px_80px_90px_90px_90px] gap-2 px-4 py-2.5 items-center text-[12.5px]" data-testid={`tracker-row-${t.user_id}`}>
                    <div className="min-w-0">
                      <p className="truncate font-medium">{t.name || '(sin nombre)'}</p>
                      <p className="text-[10.5px] text-[#8a8579] truncate">{t.email}</p>
                    </div>
                    <div className="text-right tabular-nums">{t.captures}</div>
                    <div className="text-right tabular-nums text-[#15803d]">{t.on_time}</div>
                    <div className="text-right tabular-nums text-[#991b1b]">{t.missed}</div>
                    <div className={`text-right tabular-nums font-medium ${punctualCls}`}>{t.total_slots > 0 ? `${t.on_time_pct}%` : '—'}</div>
                    <div className="text-right tabular-nums">{t.tracking_requested}</div>
                    <div className="text-right tabular-nums text-[#3b6cff]">{t.approval_rate_pct != null ? `${t.approval_rate_pct}%` : '—'}</div>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </section>

      {/* ===== Agents ===== */}
      <section className="space-y-3" data-testid="section-agents">
        <h3 className="font-serif text-[22px] tracking-tight inline-flex items-center gap-2"><ShieldCheck size={16} strokeWidth={1.6}/> Performance · Agenciados</h3>
        <AckPerfTable rows={agent_performance} kind="agent"/>
      </section>

      {/* ===== TMs ===== */}
      <section className="space-y-3" data-testid="section-tms">
        <h3 className="font-serif text-[22px] tracking-tight inline-flex items-center gap-2"><TrendingUp size={16} strokeWidth={1.6}/> Performance · Territory Managers</h3>
        <AckPerfTable rows={tm_performance} kind="tm"/>
      </section>
    </div>
  );
};

const AckPerfTable = ({ rows, kind }) => {
  if (rows.length === 0) {
    return <div className="bg-white border border-[#e8e5de] rounded-xl p-8 text-center text-[12.5px] text-[#8a8579]">Sin actividad en este rango.</div>;
  }
  return (
    <div className="bg-white border border-[#e8e5de] rounded-xl overflow-hidden">
      <div className="grid grid-cols-[1.6fr_70px_70px_70px_70px_80px_80px] gap-2 px-4 py-2.5 bg-[#f8f6f1] border-b border-[#e8e5de] text-[10.5px] uppercase tracking-wider text-[#8a8579]">
        <div>{kind === 'agent' ? 'Agenciado' : 'TM'}</div>
        <div className="text-right">&lt;30m</div>
        <div className="text-right">&lt;2h</div>
        <div className="text-right">&lt;24h</div>
        <div className="text-right">&gt;24h</div>
        <div className="text-right">Pend. actual</div>
        <div className="text-right">% Confirm.</div>
      </div>
      <div className="divide-y divide-[#f0ede5]">
        {rows.map(r => (
          <div key={r.user_id} className="grid grid-cols-[1.6fr_70px_70px_70px_70px_80px_80px] gap-2 px-4 py-2.5 items-center text-[12.5px]" data-testid={`${kind}-row-${r.user_id}`}>
            <div className="min-w-0">
              <p className="truncate font-medium">{r.name || '(sin nombre)'}</p>
              <p className="text-[10.5px] text-[#8a8579] truncate">{r.email}</p>
            </div>
            <div className="text-right tabular-nums text-[#15803d]">{r.ack_under_30m}</div>
            <div className="text-right tabular-nums text-[#3b6cff]">{r.ack_under_2h}</div>
            <div className="text-right tabular-nums text-[#92400e]">{r.ack_under_24h}</div>
            <div className="text-right tabular-nums text-[#991b1b]">{r.ack_over_24h}</div>
            <div className={`text-right tabular-nums ${r.current_pending > 0 ? 'text-[#991b1b] font-medium' : ''}`}>{r.current_pending}</div>
            <div className="text-right tabular-nums font-medium">{r.ack_pct != null ? `${r.ack_pct}%` : '—'}</div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default AdminMetrics;
